import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';

const ModernDashboard = ({
  user = {},
  dashboardStats = {},
  tickets = [],
  closedTickets = [],
  assets = [],
  users = [],
  dailyWorkData = [],
  setCurrentView = () => {}
}) => {
  // Live clock state
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Time-aware greeting
  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    if (hour < 21) return 'Good Evening';
    return 'Good Night';
  };

  const isAdmin = dashboardStats.is_admin || user.role === 'admin' || user.role === 'superadmin';

  // Safe stats calculations
  const totalTickets = dashboardStats.total_tickets ?? tickets.length ?? 0;
  const openTickets = dashboardStats.open_tickets ?? tickets.filter(t => t.status === 'open').length ?? 0;
  const inProgressTickets = dashboardStats.in_progress_tickets ?? tickets.filter(t => t.status === 'in_progress').length ?? 0;
  const closedTicketsCount = dashboardStats.closed_tickets ?? closedTickets.length ?? 0;
  
  const totalAssets = dashboardStats.total_assets ?? assets.length ?? 0;
  const totalAssetValue = dashboardStats.total_asset_value ?? assets.reduce((sum, a) => sum + (Number(a.value) || 0), 0) ?? 0;
  const totalUsers = dashboardStats.total_users ?? users.length ?? (isAdmin ? 2 : 1);

  // Daily activity metrics
  const ticketsRaised = Array.isArray(dailyWorkData) 
    ? dailyWorkData.reduce((sum, day) => sum + (Number(day.raised) || 0), 0) 
    : 0;
  const ticketsAssigned = Array.isArray(dailyWorkData) 
    ? dailyWorkData.reduce((sum, day) => sum + (Number(day.assigned) || 0), 0) 
    : 0;
  const ticketsClosedToday = Array.isArray(dailyWorkData) 
    ? dailyWorkData.reduce((sum, day) => sum + (Number(day.closed) || 0), 0) 
    : 0;
  const totalDailyActivity = ticketsRaised + ticketsAssigned + ticketsClosedToday;

  // Percentage calculations for Ticket Status
  const statusTotal = openTickets + closedTicketsCount + inProgressTickets;
  const openPct = statusTotal > 0 ? Math.round((openTickets / statusTotal) * 100) : 0;
  const closedPct = statusTotal > 0 ? Math.round((closedTicketsCount / statusTotal) * 100) : 0;
  const inProgressPct = statusTotal > 0 ? Math.round((inProgressTickets / statusTotal) * 100) : 0;

  // Daily Activity Percentages
  const raisedPct = totalDailyActivity > 0 ? Math.round((ticketsRaised / totalDailyActivity) * 100) : 0;
  const assignedPct = totalDailyActivity > 0 ? Math.round((ticketsAssigned / totalDailyActivity) * 100) : 0;
  const closedDailyPct = totalDailyActivity > 0 ? Math.round((ticketsClosedToday / totalDailyActivity) * 100) : 0;

  // Helper for SVG Donut slices
  const renderDonutSlice = (cx, cy, radius, strokeWidth, startAngle, angle, color) => {
    if (angle <= 0) return null;
    const endAngle = startAngle + angle;
    const isFullCircle = angle >= 359.9;
    
    if (isFullCircle) {
      return (
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
        />
      );
    }

    const startRad = (startAngle - 90) * (Math.PI / 180);
    const endRad = (endAngle - 90) * (Math.PI / 180);

    const x1 = cx + radius * Math.cos(startRad);
    const y1 = cy + radius * Math.sin(startRad);
    const x2 = cx + radius * Math.cos(endRad);
    const y2 = cy + radius * Math.sin(endRad);

    const largeArcFlag = angle > 180 ? 1 : 0;

    const pathData = [
      `M ${x1} ${y1}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`
    ].join(' ');

    return (
      <path
        d={pathData}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    );
  };

  const recentList = tickets.slice(0, 5);

  return (
    <div className="space-y-3.5 pb-10 font-sans text-slate-700">
      {/* 1. Compact Executive Control Ribbon */}
      <div className="bg-white rounded-2xl p-3 md:px-4 md:py-2.5 border border-slate-200/80 shadow-xs flex flex-col md:flex-row md:items-center md:justify-between gap-2.5">
        {/* Left: User Greeting & Scope */}
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-emerald-600 via-emerald-500 to-teal-500 text-white flex items-center justify-center font-bold text-sm shadow-xs flex-shrink-0">
            {(user.username || 'A').slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <h1 className="text-base md:text-lg font-bold text-slate-800 tracking-tight truncate">
              {getGreeting()}, {user.username || 'User'}
            </h1>
          </div>
        </div>

        {/* Right: Compact Live Clock (Desktop only) */}
        <div className="hidden md:flex bg-slate-50 border border-slate-200/80 rounded-xl px-3.5 py-1.5 items-center gap-3 flex-shrink-0 self-start md:self-auto shadow-2xs">
          <div className="text-right">
            <div className="text-slate-800 font-bold text-xs md:text-sm font-mono tracking-tight flex items-center justify-end gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
              {format(currentTime, 'hh:mm:ss a')}
            </div>
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              {format(currentTime, 'EEE, dd MMM yyyy')}
            </div>
          </div>
        </div>
      </div>      {/* 2. Compact 4-KPI Telemetry Deck (2x2 on Mobile, 4x1 on Desktop) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 md:gap-3.5">
        {/* Card 1: All Tickets */}
        <div 
          onClick={() => setCurrentView('tickets')}
          className="bg-white rounded-2xl p-3 md:p-4 border border-slate-200/80 shadow-xs hover:border-emerald-300 hover:shadow-md transition-all duration-200 cursor-pointer group flex flex-col justify-between"
        >
          <div className="flex items-start justify-between">
            <div>
              <span className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                {isAdmin ? 'Total Tickets' : 'My Workload'}
              </span>
              <div className="text-xl md:text-2xl font-black text-slate-800 mt-0.5 md:mt-1 tracking-tight">
                {totalTickets}
              </div>
            </div>
            <div className="w-7 h-7 md:w-8 md:h-8 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition-colors duration-200 flex-shrink-0">
              <svg className="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
            </div>
          </div>
          <div className="flex items-center justify-between mt-2 md:mt-3 pt-2 border-t border-slate-100 text-[10px] md:text-[11px]">
            <span className="text-emerald-700 font-semibold flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
              <span>{openTickets} open</span>
            </span>
            <span className="text-slate-400 font-semibold group-hover:text-emerald-600 transition-colors hidden sm:flex items-center gap-0.5">
              Explore &rarr;
            </span>
          </div>
        </div>

        {/* Card 2: Open Tickets Requiring Action */}
        <div 
          onClick={() => setCurrentView('tickets')}
          className="bg-white rounded-2xl p-3 md:p-4 border border-slate-200/80 shadow-xs hover:border-rose-300 hover:shadow-md transition-all duration-200 cursor-pointer group flex flex-col justify-between"
        >
          <div className="flex items-start justify-between">
            <div>
              <span className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                {isAdmin ? 'In-Flight' : 'Pending'}
              </span>
              <div className="text-xl md:text-2xl font-black text-rose-600 mt-0.5 md:mt-1 tracking-tight">
                {openTickets + inProgressTickets}
              </div>
            </div>
            <div className="w-7 h-7 md:w-8 md:h-8 rounded-xl bg-rose-50 text-rose-600 border border-rose-100 flex items-center justify-center group-hover:bg-rose-600 group-hover:text-white transition-colors duration-200 flex-shrink-0">
              <svg className="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
          <div className="flex items-center justify-between mt-2 md:mt-3 pt-2 border-t border-slate-100 text-[10px] md:text-[11px]">
            <span className="text-rose-600 font-semibold flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-rose-500 rounded-full"></span>
              <span>{inProgressTickets} active</span>
            </span>
            <span className="text-slate-400 font-semibold group-hover:text-rose-600 transition-colors hidden sm:flex items-center gap-0.5">
              Review &rarr;
            </span>
          </div>
        </div>

        {/* Card 3: All Assets */}
        <div 
          onClick={() => setCurrentView('assets')}
          className="bg-white rounded-2xl p-3 md:p-4 border border-slate-200/80 shadow-xs hover:border-emerald-300 hover:shadow-md transition-all duration-200 cursor-pointer group flex flex-col justify-between"
        >
          <div className="flex items-start justify-between">
            <div>
              <span className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                {isAdmin ? 'Managed Assets' : 'My Hardware'}
              </span>
              <div className="text-xl md:text-2xl font-black text-slate-800 mt-0.5 md:mt-1 tracking-tight">
                {totalAssets}
              </div>
            </div>
            <div className="w-7 h-7 md:w-8 md:h-8 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition-colors duration-200 flex-shrink-0">
              <svg className="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <div className="flex items-center justify-between mt-2 md:mt-3 pt-2 border-t border-slate-100 text-[10px] md:text-[11px]">
            <span className="text-emerald-600 font-semibold flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
              <span>{assets.filter(a => a.assigned_to).length || 0} assigned</span>
            </span>
            <span className="text-slate-400 font-semibold group-hover:text-emerald-600 transition-colors hidden sm:flex items-center gap-0.5">
              Inventory &rarr;
            </span>
          </div>
        </div>

        {/* Card 4: Total Asset Valuation */}
        <div 
          onClick={() => setCurrentView('assets')}
          className="bg-white rounded-2xl p-3 md:p-4 border border-slate-200/80 shadow-xs hover:border-purple-300 hover:shadow-md transition-all duration-200 cursor-pointer group flex flex-col justify-between"
        >
          <div className="flex items-start justify-between">
            <div>
              <span className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                {isAdmin ? 'Asset Valuation' : 'Assigned Capex'}
              </span>
              <div className="text-xl md:text-2xl font-black text-slate-800 mt-0.5 md:mt-1 tracking-tight truncate">
                ₹{Number(totalAssetValue).toLocaleString('en-IN')}
              </div>
            </div>
            <div className="w-7 h-7 md:w-8 md:h-8 rounded-xl bg-purple-50 text-purple-600 border border-purple-100 flex items-center justify-center group-hover:bg-purple-600 group-hover:text-white transition-colors duration-200 flex-shrink-0">
              <svg className="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 8h6m-5 0a3 3 0 110 6H9l3 3m-3-6h6m6 1a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div className="flex items-center justify-between mt-2 md:mt-3 pt-2 border-t border-slate-100 text-[10px] md:text-[11px]">
            <span className="text-purple-600 font-semibold flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
              <span>Audited</span>
            </span>
            <span className="text-slate-400 font-semibold group-hover:text-purple-600 transition-colors hidden sm:flex items-center gap-0.5">
              Breakdown &rarr;
            </span>
          </div>
        </div>
      </div>

      {/* 3. Compact Analytics Matrix (3 Columns) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3.5">
        {/* Widget 1: Ticket Status Distribution */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                  </svg>
                </div>
                <h3 className="text-xs font-bold text-slate-800">Ticket Status Matrix</h3>
              </div>
              <button 
                onClick={() => setCurrentView('tickets')}
                className="text-[11px] font-bold text-blue-600 hover:text-blue-700 hover:underline"
              >
                View
              </button>
            </div>

            {/* Donut Chart */}
            <div className="relative flex items-center justify-center my-4">
              <svg width="140" height="140" viewBox="0 0 140 140" className="transform -rotate-90">
                <circle
                  cx="70"
                  cy="70"
                  r="48"
                  fill="none"
                  stroke="#F1F5F9"
                  strokeWidth="14"
                />
                {renderDonutSlice(70, 70, 48, 14, 0, (closedPct / 100) * 360, '#10B981')}
                {renderDonutSlice(70, 70, 48, 14, (closedPct / 100) * 360, (openPct / 100) * 360, '#EF4444')}
                {renderDonutSlice(70, 70, 48, 14, ((closedPct + openPct) / 100) * 360, (inProgressPct / 100) * 360, '#F59E0B')}
              </svg>

              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-xl font-bold text-slate-800 tracking-tight">
                  {totalTickets}
                </span>
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                  TOTAL
                </span>
              </div>
            </div>
          </div>

          {/* Compact Distribution Pills */}
          <div className="grid grid-cols-3 gap-1.5 pt-2 border-t border-slate-100">
            <div className="bg-slate-50 border border-slate-200/60 p-1.5 rounded-xl text-center">
              <div className="flex items-center justify-center gap-1 text-[10px] text-slate-500 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                <span>Open</span>
              </div>
              <span className="text-xs font-bold text-slate-800 block mt-0.5">{openTickets}</span>
            </div>

            <div className="bg-slate-50 border border-slate-200/60 p-1.5 rounded-xl text-center">
              <div className="flex items-center justify-center gap-1 text-[10px] text-slate-500 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                <span>In-Prog</span>
              </div>
              <span className="text-xs font-bold text-slate-800 block mt-0.5">{inProgressTickets}</span>
            </div>

            <div className="bg-slate-50 border border-slate-200/60 p-1.5 rounded-xl text-center">
              <div className="flex items-center justify-center gap-1 text-[10px] text-slate-500 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                <span>Closed</span>
              </div>
              <span className="text-xs font-bold text-slate-800 block mt-0.5">{closedTicketsCount}</span>
            </div>
          </div>
        </div>

        {/* Widget 2: Daily Operations Velocity */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-orange-50 text-orange-600 border border-orange-100 flex items-center justify-center">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xs font-bold text-slate-800">Daily Operations Activity</h3>
              </div>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                Active
              </span>
            </div>

            {/* Donut Chart */}
            <div className="relative flex items-center justify-center my-4">
              <svg width="140" height="140" viewBox="0 0 140 140" className="transform -rotate-90">
                <circle
                  cx="70"
                  cy="70"
                  r="48"
                  fill="none"
                  stroke="#F1F5F9"
                  strokeWidth="14"
                />
                {renderDonutSlice(70, 70, 48, 14, 0, (raisedPct / 100) * 360, '#3B82F6')}
                {renderDonutSlice(70, 70, 48, 14, (raisedPct / 100) * 360, (assignedPct / 100) * 360, '#10B981')}
                {renderDonutSlice(70, 70, 48, 14, ((raisedPct + assignedPct) / 100) * 360, (closedDailyPct / 100) * 360, '#F59E0B')}
              </svg>

              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-xl font-bold text-slate-800 tracking-tight">
                  {totalDailyActivity}
                </span>
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                  ACTIONS
                </span>
              </div>
            </div>
          </div>

          {/* Compact Distribution Pills */}
          <div className="grid grid-cols-3 gap-1.5 pt-2 border-t border-slate-100">
            <div className="bg-slate-50 border border-slate-200/60 p-1.5 rounded-xl text-center">
              <div className="flex items-center justify-center gap-1 text-[10px] text-slate-500 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                <span>Raised</span>
              </div>
              <span className="text-xs font-bold text-slate-800 block mt-0.5">{ticketsRaised}</span>
            </div>

            <div className="bg-slate-50 border border-slate-200/60 p-1.5 rounded-xl text-center">
              <div className="flex items-center justify-center gap-1 text-[10px] text-slate-500 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                <span>Assigned</span>
              </div>
              <span className="text-xs font-bold text-slate-800 block mt-0.5">{ticketsAssigned}</span>
            </div>

            <div className="bg-slate-50 border border-slate-200/60 p-1.5 rounded-xl text-center">
              <div className="flex items-center justify-center gap-1 text-[10px] text-slate-500 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                <span>Resolved</span>
              </div>
              <span className="text-xs font-bold text-slate-800 block mt-0.5">{ticketsClosedToday}</span>
            </div>
          </div>
        </div>

        {/* Widget 3: Organization & Infrastructure Snapshot */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-purple-50 text-purple-600 border border-purple-100 flex items-center justify-center">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-xs font-bold text-slate-800">Tenant Environment</h3>
              </div>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                v2.0
              </span>
            </div>

            <div className="space-y-2 mt-3 text-xs">
              <div className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200/60 rounded-xl">
                <span className="text-slate-500 font-medium">Organization</span>
                <span className="font-bold text-slate-800 truncate max-w-[140px]">{user.client_name || 'DeskZen Global'}</span>
              </div>

              <div className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200/60 rounded-xl">
                <span className="text-slate-500 font-medium">Active Roster</span>
                <span className="font-bold text-slate-800">{totalUsers} Members</span>
              </div>

              <div className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200/60 rounded-xl">
                <span className="text-slate-500 font-medium">Infrastructure</span>
                <span className="font-bold text-emerald-700 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  99.98% Uptime
                </span>
              </div>
            </div>
          </div>

          <div className="pt-2.5 mt-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
            <span>Security Isolation</span>
            <span className="font-bold text-emerald-600">Enforced & Active</span>
          </div>
        </div>
      </div>

      {/* 4. Bottom Deck: Live Timeline & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3.5">
        {/* Left: Recent Activity Feed (Col Span 2) */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-4 border border-slate-200/80 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between pb-2.5 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xs md:text-sm font-bold text-slate-800">Recent Service Activity</h3>
              </div>
              {recentList.length > 0 && (
                <button 
                  onClick={() => setCurrentView('tickets')}
                  className="text-xs font-bold text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1"
                >
                  <span>View All Tickets</span>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>

            {/* List */}
            {recentList.length > 0 ? (
              <div className="divide-y divide-slate-100 mt-1">
                {recentList.map((ticket, index) => (
                  <div 
                    key={ticket.id || index}
                    onClick={() => setCurrentView('tickets')}
                    className="py-2.5 px-2 hover:bg-slate-50/80 rounded-xl transition-colors flex items-center justify-between cursor-pointer group"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        ticket.priority === 'high' ? 'bg-red-500' :
                        ticket.priority === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'
                      }`} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-mono font-bold text-slate-500 bg-slate-100 px-1 py-0.2 rounded border border-slate-200">
                            #{ticket.id ? ticket.id.slice(0, 6) : 'TKT'}
                          </span>
                          <span className="text-xs font-bold text-slate-800 group-hover:text-blue-600 transition-colors truncate">
                            {ticket.title}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-400 truncate mt-0.5">
                          {ticket.created_at ? format(new Date(ticket.created_at), 'MMM dd, yyyy') : 'Recent'} • By {ticket.created_by_username || 'User'}
                        </p>
                      </div>
                    </div>

                    <div className="flex-shrink-0 ml-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold capitalize ${
                        ticket.status === 'open' 
                          ? 'bg-rose-50 text-rose-700 border border-rose-200' 
                          : ticket.status === 'in_progress' 
                          ? 'bg-amber-50 text-amber-700 border border-amber-200' 
                          : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      }`}>
                        {ticket.status === 'open' ? 'Open' : ticket.status === 'in_progress' ? 'In Progress' : 'Closed'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 px-4 text-slate-400 text-xs">
                No recent activity logged.
              </div>
            )}
          </div>
        </div>

        {/* Right: Quick Command Center (Col Span 1) */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 pb-2.5 border-b border-slate-100">
              <div className="w-6 h-6 rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-100 flex items-center justify-center">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xs md:text-sm font-bold text-slate-800">Operations Hub</h3>
            </div>

            <div className="grid grid-cols-1 gap-2 mt-3">
              <button
                onClick={() => setCurrentView('create-ticket')}
                className="w-full flex items-center justify-between p-2.5 rounded-xl bg-slate-50 hover:bg-emerald-50/80 text-slate-800 hover:text-emerald-700 border border-slate-200 hover:border-emerald-300 font-semibold text-xs transition-all group"
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-6 h-6 rounded-lg bg-gradient-to-tr from-emerald-600 to-teal-600 text-white flex items-center justify-center">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                  <span>Create Ticket</span>
                </div>
                <svg className="w-3.5 h-3.5 text-slate-400 group-hover:text-emerald-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <button
                onClick={() => setCurrentView('assets')}
                className="w-full flex items-center justify-between p-2.5 rounded-xl bg-slate-50 hover:bg-emerald-50/80 text-slate-800 hover:text-emerald-700 border border-slate-200 hover:border-emerald-300 font-semibold text-xs transition-all group"
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-6 h-6 rounded-lg bg-emerald-600 text-white flex items-center justify-center">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <span>Asset Inventory</span>
                </div>
                <svg className="w-3.5 h-3.5 text-slate-400 group-hover:text-emerald-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <button
                onClick={() => setCurrentView('teams')}
                className="w-full flex items-center justify-between p-2.5 rounded-xl bg-slate-50 hover:bg-purple-50/80 text-slate-800 hover:text-purple-700 border border-slate-200 hover:border-purple-300 font-semibold text-xs transition-all group"
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-6 h-6 rounded-lg bg-purple-600 text-white flex items-center justify-center">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <span>Team Workspaces</span>
                </div>
                <svg className="w-3.5 h-3.5 text-slate-400 group-hover:text-purple-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <button
                onClick={() => setCurrentView('reports')}
                className="w-full flex items-center justify-between p-2.5 rounded-xl bg-slate-50 hover:bg-amber-50/80 text-slate-800 hover:text-amber-700 border border-slate-200 hover:border-amber-300 font-semibold text-xs transition-all group"
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-6 h-6 rounded-lg bg-amber-500 text-white flex items-center justify-center">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <span>Analytics & Reports</span>
                </div>
                <svg className="w-3.5 h-3.5 text-slate-400 group-hover:text-amber-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernDashboard;


